"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight, Menu } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { UserMenu } from "@/components/UserMenu";
import { useUser } from "@/hooks/useUser";
import { useAppConfig } from "@/lib/config";
interface HeaderLink {
  href: string;
  label: string;
  external?: boolean;
}
function useHeaderLinks() {
  const $ = _c(6);
  const config = useAppConfig();
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      href: "/#how-it-works",
      label: "How it works"
    };
    t1 = {
      href: "/#dispatch",
      label: "Dispatch"
    };
    t2 = {
      href: "/#faq",
      label: "FAQ"
    };
    t3 = {
      href: "/resources",
      label: "Guides & tools"
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  const t4 = `${config.uiUrl}/rankings`;
  let t5;
  if ($[4] !== t4) {
    t5 = [t0, t1, t2, t3, {
      href: t4,
      label: "Model rankings",
      external: true
    }];
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function HeaderNavLink(t0) {
  const $ = _c(10);
  const {
    link,
    className,
    onNavigate
  } = t0;
  if (link.external) {
    let t1;
    if ($[0] !== className || $[1] !== link.href || $[2] !== link.label || $[3] !== onNavigate) {
      t1 = <a href={link.href} className={className} onClick={onNavigate}>{link.label}</a>;
      $[0] = className;
      $[1] = link.href;
      $[2] = link.label;
      $[3] = onNavigate;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== className || $[6] !== link.href || $[7] !== link.label || $[8] !== onNavigate) {
    t1 = <Link href={link.href} className={className} onClick={onNavigate}>{link.label}</Link>;
    $[5] = className;
    $[6] = link.href;
    $[7] = link.label;
    $[8] = onNavigate;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
}
function MobileMenu(t0) {
  const $ = _c(17);
  const {
    links,
    signedIn
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const close = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SheetTrigger asChild={true}><Button variant="ghost" size="icon" aria-label="Open menu" className="lg:hidden"><Menu className="size-5" /></Button></SheetTrigger>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SheetHeader className="border-b px-5 py-4"><SheetTitle className="flex items-center gap-2.5"><Logo className="size-6" /><span className="font-display text-base font-black tracking-tight">AIRSIDE</span></SheetTitle><SheetDescription className="sr-only">Site navigation</SheetDescription></SheetHeader>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== links) {
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = link => <HeaderNavLink key={link.href} link={link} onNavigate={close} className="text-muted-foreground hover:text-foreground hover:bg-accent flex items-center rounded-md px-3 py-2.5 text-sm font-medium transition-colors" />;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    t4 = links.map(t5);
    $[3] = links;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <nav aria-label="Mobile navigation" className="flex flex-col gap-1 px-3 py-4">{t4}</nav>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex items-center justify-between"><span className="text-muted-foreground font-mono text-[0.7rem] tracking-wider uppercase">Theme</span><ThemeToggle size="compact" /></div>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== signedIn) {
    t7 = <div className="mt-auto flex flex-col gap-3 border-t px-5 py-5">{t6}{signedIn ? <Button asChild={true}><Link href="/dashboard" onClick={close}>Open console</Link></Button> : <><Button asChild={true}><Link href="/signup" onClick={close}>Claim your carrier code</Link></Button><Button asChild={true} variant="outline"><Link href="/login" onClick={close}>Sign in</Link></Button></>}</div>;
    $[9] = signedIn;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <SheetContent side="right" className="w-72 gap-0 p-0">{t3}{t5}{t7}</SheetContent>;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== open || $[15] !== t8) {
    t9 = <Sheet open={open} onOpenChange={setOpen}>{t2}{t8}</Sheet>;
    $[14] = open;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
export function Header() {
  const $ = _c(21);
  const {
    user
  } = useUser();
  const config = useAppConfig();
  const links = useHeaderLinks();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Link href="/" aria-label="Airside home" className="flex shrink-0 items-center gap-2.5"><Logo /><span className="font-display hidden text-lg font-black tracking-tight sm:inline">AIRSIDE</span></Link>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ArrowUpRight className="size-3" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== config.uiUrl) {
    t2 = <div className="flex min-w-0 items-center gap-3">{t0}<a href={config.uiUrl} className="text-muted-foreground hover:text-foreground hidden items-center gap-0.5 whitespace-nowrap font-mono text-[0.7rem] tracking-wider uppercase transition-colors md:inline-flex">by LLM Gateway{t1}</a></div>;
    $[2] = config.uiUrl;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== links) {
    t3 = links.map(_temp);
    $[4] = links;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <nav aria-label="Main navigation" className="hidden flex-1 items-center justify-center gap-1 lg:flex">{t3}</nav>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ThemeToggle size="compact" className="hidden md:inline-flex" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== user) {
    t6 = user ? <><Button asChild={true} size="sm"><Link href="/dashboard">Open console</Link></Button><UserMenu variant="compact" /></> : <><Button asChild={true} variant="ghost" size="sm" className="hidden sm:inline-flex"><Link href="/login">Sign in</Link></Button><Button asChild={true} size="sm"><Link href="/signup"><span className="sm:hidden">List API</span><span className="hidden sm:inline">Claim your carrier code</span></Link></Button></>;
    $[9] = user;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = !!user;
  let t8;
  if ($[11] !== links || $[12] !== t7) {
    t8 = <MobileMenu links={links} signedIn={t7} />;
    $[11] = links;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t8) {
    t9 = <div className="ml-auto flex shrink-0 items-center gap-2 lg:ml-0">{t5}{t6}{t8}</div>;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t2 || $[18] !== t4 || $[19] !== t9) {
    t10 = <header className="border-border/60 bg-background/85 supports-backdrop-filter:bg-background/70 sticky top-0 z-40 border-b backdrop-blur"><div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 sm:px-6">{t2}{t4}{t9}</div></header>;
    $[17] = t2;
    $[18] = t4;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
function _temp(link) {
  return <HeaderNavLink key={link.href} link={link} className="text-muted-foreground hover:text-foreground hover:bg-accent/60 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors" />;
}